import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useStorageState } from 'react-storage-hooks';

import NewsRibbonBox from './NewsRibbonBox';

type NewsRibbonContextProps = {
  setType: React.Dispatch<React.SetStateAction<'global' | 'local' | ''>>;
  type: 'global' | 'local' | '';
  hash: string;
  setHash: React.Dispatch<React.SetStateAction<string>>;
  useGlobal: boolean;
  setUseGlobal: React.Dispatch<React.SetStateAction<boolean>>;
  closedRibbons: string[];
  setClosedRibbons: React.Dispatch<React.SetStateAction<string[]>>;
};

const NewsRibbonContext = createContext<NewsRibbonContextProps>({
  setType: () => {},
  type: '',
  hash: '',
  setUseGlobal: () => {},
  setHash: () => {},
  useGlobal: false,
  closedRibbons: [],
  setClosedRibbons: () => {},
});

export const NewsRibbonProvider = ({ children }: PropsWithChildren<any>) => {
  const [hash, setHash] = useState<string>('');
  const [type, setType] = useState<NewsRibbonContextProps['type']>('');
  const [useGlobal, setUseGlobal] = useState(false);
  const [closedRibbons, setClosedRibbons] = useStorageState<string[]>(
    sessionStorage,
    'CLOSED-RIBBONS',
    [],
  );

  return (
    <NewsRibbonContext.Provider
      value={{
        type,
        setType,
        useGlobal,
        setUseGlobal,
        hash,
        setHash,
        closedRibbons,
        setClosedRibbons,
      }}
    >
      <NewsRibbonBox />
      {children}
    </NewsRibbonContext.Provider>
  );
};

export const useNewsRibbon = ({
  hash,
  type,
  useGlobal,
}: {
  hash: string;
  type: 'local' | 'global';
  useGlobal: boolean;
}) => {
  const {
    type: currentType,
    setType,
    setHash,
    useGlobal: currentUseGlobal,
    closedRibbons,
  } = useContext(NewsRibbonContext);
  const isClosed = useMemo(
    () => !!closedRibbons?.find((key) => key === hash),
    [closedRibbons, hash],
  );
  const visible = useMemo(() => (type ? currentType === type : true), [currentType, type]);
  const nextUseGlobal = useMemo(() => useGlobal || currentUseGlobal, [currentUseGlobal, useGlobal]);

  useEffect(() => {
    if (isClosed) return;
    if (currentType === type) return;

    if (type === 'local') {
      if (currentType === 'global' && nextUseGlobal) return;
    }

    if (type === 'global') {
      if (currentType === 'local' && !nextUseGlobal) return;
    }

    setType(type);
    setHash(hash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { visible };
};

export const useCurrentNewsRibbon = () => {
  const { hash, closedRibbons, setClosedRibbons, setType } = useContext(NewsRibbonContext);
  const [closed, setClosed] = useState(false);

  const handleClose = useCallback(
    (delay = 0) => {
      setClosed(true);
      setTimeout(() => {
        setClosedRibbons([...closedRibbons, hash]);
        setType('');
      }, delay);
    },
    [closedRibbons, hash, setClosedRibbons, setType],
  );

  return { close: handleClose, closed };
};
